import React, { useEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import AppHeader from './components/AppHeader.jsx'
import ComparisonColumnControls from './components/ComparisonColumnControls.jsx'
import HoldingTable from './components/HoldingTable.jsx'
import StatusMessage from './components/StatusMessage.jsx'
import StockFilterModal from './components/StockFilterModal.jsx'
import {
  getAllStocks,
  updatePreferences,
  createDefaultStorageState,
  hasUserSettings,
  loadUserSettings,
  saveUserSettings,
} from './services/holdingStorage.js'
import {
  DATA_FILE_URL,
  MOCK_DATA_FILE_URL,
  loadTrackerDataFile,
  updateTrackerDataFile,
} from './services/trackerDataFile.js'
import { buildHoldingRows } from './utils/comparison.js'
import { getNextSort, sortHoldingRows } from './utils/sorting.js'

function latestSnapshotOf(snapshots) {
  return snapshots.length === 0
    ? null
    : [...snapshots].sort((a, b) => a.date.localeCompare(b.date)).at(-1)
}

function App() {
  const [userSettings, setUserSettings] = useState(() => loadUserSettings())
  const userSettingsRef = useRef(userSettings)
  const hasUserSettingsRef = useRef(hasUserSettings())
  const [trackerState, setTrackerState] = useState(() => ({
    ...createDefaultStorageState(),
    preferences: userSettings.preferences,
  }))
  const [updateStatus, setUpdateStatus] = useState({ type: 'idle', message: '' })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isMockData, setIsMockData] = useState(false)
  const dataFileUrl = isMockData ? MOCK_DATA_FILE_URL : DATA_FILE_URL

  useEffect(() => {
    userSettingsRef.current = userSettings
  }, [userSettings])

  useEffect(() => {
    let isMounted = true
    setUpdateStatus({ type: 'idle', message: '' })

    loadTrackerDataFile(dataFileUrl)
      .then((fileState) => {
        if (isMounted) {
          const settings = hasUserSettingsRef.current
            ? userSettingsRef.current
            : {
                ...userSettingsRef.current,
                preferences: fileState.preferences,
              }
          if (!hasUserSettingsRef.current) {
            saveUserSettings(localStorage, settings)
            hasUserSettingsRef.current = true
            setUserSettings(settings)
          }
          setTrackerState({
            ...fileState,
            preferences: settings.preferences,
          })
        }
      })
      .catch((error) => {
        if (isMounted) {
          setUpdateStatus({
            type: 'error',
            message: error.message || '讀取資料檔失敗',
          })
        }
      })

    return () => {
      isMounted = false
    }
  }, [dataFileUrl])

  const latestSnapshot = useMemo(
    () => latestSnapshotOf(trackerState.snapshots),
    [trackerState.snapshots],
  )
  const allStocks = useMemo(
    () => getAllStocks(trackerState.snapshots),
    [trackerState.snapshots],
  )
  const rows = useMemo(() => {
    const displayRows = buildHoldingRows(trackerState.snapshots, trackerState.preferences)
    return sortHoldingRows(displayRows, trackerState.preferences.sort)
  }, [trackerState])

  const handleUpdate = async () => {
    setUpdateStatus({ type: 'loading', message: '資料更新中' })

    try {
      const previousDates = new Set(trackerState.snapshots.map((item) => item.date))
      const nextState = await updateTrackerDataFile(trackerState)
      const latest = latestSnapshotOf(nextState.snapshots)
      const existed = latest ? previousDates.has(latest.date) : false
      setTrackerState({
        ...nextState,
        preferences: userSettings.preferences,
      })
      setUpdateStatus({
        type: 'success',
        message: existed ? '已重新更新該日期資料' : '更新成功',
      })
    } catch (error) {
      setUpdateStatus({
        type: 'error',
        message: error.message || '更新失敗',
      })
    }
  }

  const handlePreferenceChange = (preferences) => {
    const nextState = updatePreferences(trackerState, preferences)
    const nextSettings = {
      ...userSettings,
      preferences: nextState.preferences,
    }
    saveUserSettings(localStorage, nextSettings)
    hasUserSettingsRef.current = true
    setUserSettings(nextSettings)
    setTrackerState(nextState)
  }

  const handleSort = (field) => {
    handlePreferenceChange({
      sort: getNextSort(trackerState.preferences.sort, field),
    })
  }

  const handleFilterApply = (selectedStockCodes) => {
    handlePreferenceChange({ selectedStockCodes })
    setIsFilterOpen(false)
  }

  const handleThemeToggle = () => {
    const nextTheme = userSettings.theme === 'light' ? 'dark' : 'light'
    const nextSettings = {
      ...userSettings,
      theme: nextTheme,
    }
    saveUserSettings(localStorage, nextSettings)
    hasUserSettingsRef.current = true
    setUserSettings(nextSettings)
  }

  return (
    <div className="App" data-theme={userSettings.theme}>
      <AppHeader
        latestSnapshot={latestSnapshot}
        snapshotCount={trackerState.snapshots.length}
        isUpdating={updateStatus.type === 'loading'}
        isUpdateDisabled={updateStatus.type === 'loading' || isMockData}
        isMockData={isMockData}
        theme={userSettings.theme}
        onUpdate={handleUpdate}
        onOpenFilter={() => setIsFilterOpen(true)}
        onMockDataChange={setIsMockData}
        onThemeToggle={handleThemeToggle}
      />

      <main className="app-main">
        <StatusMessage status={updateStatus} />

        <ComparisonColumnControls
          configs={trackerState.preferences.comparisonConfigs}
          trendDays={trackerState.preferences.trendDays}
          onChange={(comparisonConfigs) => handlePreferenceChange({ comparisonConfigs })}
          onTrendDaysChange={(trendDays) => handlePreferenceChange({ trendDays })}
        />

        <HoldingTable
          rows={rows}
          comparisonConfigs={trackerState.preferences.comparisonConfigs}
          sort={trackerState.preferences.sort}
          onSort={handleSort}
          trendMetric={trackerState.preferences.trendMetric}
          onTrendMetricToggle={() => handlePreferenceChange({
            trendMetric: trackerState.preferences.trendMetric === 'weight' ? 'shares' : 'weight',
          })}
        />
      </main>

      {isFilterOpen && (
        <StockFilterModal
          stocks={allStocks}
          selectedStockCodes={trackerState.preferences.selectedStockCodes}
          onApply={handleFilterApply}
          onClose={() => setIsFilterOpen(false)}
        />
      )}
    </div>
  )
}

export default App
